import { Component, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { ClaimKey } from '../../../../models/api/core/claim-key';
import {
  OverpaymentSummary,
  OverpaymentTotals,
} from '../../../../models/api/view/claim/financials/overpayments/overpayment-summary';
import { Action } from '../../../../models/core/action';
import { FormatClaimPipe } from '../../../../pipes/format-claim.pipe';
import { BaseComponent } from '../../../base/base.component';
import { WatchButtonComponent } from '../../../core/buttons/watch-button.component';
import { PercentBarChartComponent } from '../../../core/chart/percent-bar-chart.component';
import { SelectComponent } from '../../../core/controls/select.component';
import { LinkLabelComponent } from '../../../core/labels/link-label.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { TableComponent } from '../../../core/table/table.component';

@Component({
  selector: 'app-overpayments',
  imports: [
    TableComponent,
    ColumnComponent,
    RouterModule,
    PageHeaderComponent,
    SelectComponent,
    PercentBarChartComponent,
    WatchButtonComponent,
    FormatClaimPipe,
    LinkLabelComponent,
  ],
  template: `
    <app-page-header label="Overpayments">
      <ng-template #right>
        <div class="tw-flex tw-items-center tw-w-64">
          <div class="tw-grow">
            @if (level.value !== 'claim') {
              <app-select
                [options]="levels"
                optionLabel="label"
                optionValue="key"
                [virtualScroll]="false"
                [control]="level"
                label="View"
                [multiple]="false"
                [filter]="false"
                [showClear]="false"
                [showHelpText]="false"
              />
            }
          </div>
        </div>
      </ng-template>
    </app-page-header>

    <div class="tw-flex tw-pt-2 tw-items-center tw-justify-center">
      <div class="tw-w-full md:tw-basis-2/3 lg:tw-basis-2/5">
        <app-percent-bar-chart
          totalLabel="Total balance"
          [totalValue]="totals.balance"
          [totalPercent]="totals.totalPercent"
          leftLabel="Recovered"
          [leftValue]="totals.recovered"
          rightLabel="Overpaid"
          [rightValue]="totals.overpaid"
          [hidden]="overpayments.length === 0"
        />
      </div>
    </div>
    <div class="tw-flex tw-gap-8">
      <div class="tw-grow tw-min-w-0">
        <div class="tw-w-full tw-overflow-x-auto">
          <app-table
            [dataSource]="overpayments"
            [pageSize]="10"
            [scrollable]="true"
            dataKey="id"
            recordLabel="Overpayment"
            [sortField]="'balance'"
          >
            <app-column name="reasons" label="reason">
              <ng-template let-data>
                @if (data.detailCount > 0) {
                  <app-link-label
                    (click)="
                      view.navigateCurrentClaim([
                        'overpayment-detail',
                        data.masterGroupId,
                      ])
                    "
                    >{{ data.reasons }}</app-link-label
                  >
                } @else {
                  {{ data.reasons }}
                }
              </ng-template>
            </app-column>
            <app-column name="source" label="source" />
            <app-column name="from" label="from" format="d" />
            <app-column name="through" label="through" format="d" />
            <app-column name="recoveryClaim" label="recovery Claim">
              <ng-template let-data>
                {{ data.recoveryClaim | formatClaim: claim.fileNumFormat }}
                @if (data.recoveryClaim) {
                  <app-watch-button
                    [recordSource]="claim.recordSource"
                    [recordId]="claim.recordId"
                  />
                }
              </ng-template>
            </app-column>
            <app-column name="overpaid" label="overpaid" format="c" />
            <app-column name="recovered" label="recovered" format="c" />
            <app-column name="balance" label="balance" format="c" />
          </app-table>
        </div>
      </div>
    </div>
  `,
  styles: ``,
})
export class OverpaymentsComponent extends BaseComponent implements OnInit {
  claim = this.view.currentClaim();
  level = new FormControl<string>('claim');

  levels: Action[] = [
    { key: 'claim', label: 'Claim' },
    { key: 'all', label: 'All' },
  ];

  totals: OverpaymentTotals = new OverpaymentTotals();

  overpayments: OverpaymentSummary[] = [];

  async ngOnInit(): Promise<void> {
    const claimKey: ClaimKey = {
      recordId: this.claim.recordId,
      recordSource: this.claim.recordSource,
    };
    const response = await this.api.overpayments.get(claimKey);

    if (this.level.value === 'claim') {
      this.overpayments = this.setRecoveryClaim(
        response.filter((item) => item.altFileUid === 0),
        'claim',
      );
    } else {
      this.overpayments = this.setRecoveryClaim(response, 'all');
    }

    this.totals = this.calculateTotals(this.overpayments);

    this.dispose.subs = this.level.valueChanges.subscribe((value) => {
      if (value === 'claim') {
        this.overpayments = this.setRecoveryClaim(
          response.filter((item) => item.altFileUid === 0),
          'claim',
        );
      } else if (value === 'all') {
        this.overpayments = this.setRecoveryClaim(response, 'all');
      }
      this.totals = this.calculateTotals(this.overpayments);
    });

    this.view.hideLoading();
  }

  setRecoveryClaim(
    items: OverpaymentSummary[],
    filterBy: string,
  ): OverpaymentSummary[] {
    return items.map((item) => ({
      ...item,
      recoveryClaim:
        filterBy === 'claim' ? this.claim.fileNumber : item.altFileNum,
    }));
  }

  calculateTotals(items: OverpaymentSummary[]) {
    if (items.length === 0) {
      return new OverpaymentTotals();
    }
    const totals = new OverpaymentTotals();
    totals.overpaid = items.reduce((sum, item) => sum + item.overpaid, 0);
    totals.recovered = items.reduce((sum, item) => sum + item.recovered, 0);
    totals.balance = items.reduce((sum, item) => sum + item.balance, 0);
    return totals;
  }
}
